'use client';

import { useCallback } from 'react';
import Icon from '@/components/ui/AppIcon';
import { QuoteFormData } from './SmartQuoteForm';

interface FileUploadZoneProps {
  formData: QuoteFormData;
  updateFormData: (updates: Partial<QuoteFormData>) => void;
}

export default function FileUploadZone({ formData, updateFormData }: FileUploadZoneProps) {
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      updateFormData({ files: [...formData.files, ...newFiles] });
    }
  };

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      if (e.dataTransfer.files) {
        const newFiles = Array.from(e.dataTransfer.files);
        updateFormData({ files: [...formData.files, ...newFiles] });
      }
    },
    [formData.files, updateFormData]
  );

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const removeFile = (index: number) => {
    const newFiles = formData.files.filter((_, i) => i !== index);
    updateFormData({ files: newFiles });
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h2 className="text-2xl font-headline font-bold text-foreground">
          Upload Technical Drawings
        </h2>
        <p className="text-muted-foreground font-body">
          Upload your CAD files (DXF, STEP, DWG) or technical drawings. Multiple files are supported.
        </p>
      </div>

      {/* Upload Zone */}
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        className="border-2 border-dashed border-border rounded-xl p-12 text-center hover:border-primary hover:bg-primary/5 transition-all duration-base cursor-pointer"
      >
        <input
          type="file"
          id="file-upload"
          multiple
          accept=".dxf,.step,.stp,.dwg,.pdf,.png,.jpg,.jpeg"
          onChange={handleFileChange}
          className="hidden"
        />
        <label htmlFor="file-upload" className="cursor-pointer">
          <div className="flex flex-col items-center space-y-4">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
              <Icon name="CloudArrowUpIcon" size={32} variant="outline" className="text-primary" />
            </div>
            <div className="space-y-2">
              <div className="text-lg font-cta font-bold text-foreground">
                Drag and drop files here
              </div>
              <div className="text-sm text-muted-foreground font-body">
                or click to browse from your computer
              </div>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-2 text-xs text-muted-foreground font-body">
              <span className="px-3 py-1 bg-muted rounded-full">DXF</span>
              <span className="px-3 py-1 bg-muted rounded-full">STEP</span>
              <span className="px-3 py-1 bg-muted rounded-full">DWG</span>
              <span className="px-3 py-1 bg-muted rounded-full">PDF</span>
              <span className="px-3 py-1 bg-muted rounded-full">Images</span>
            </div>
          </div>
        </label>
      </div>

      {/* Uploaded Files List */}
      {formData.files.length > 0 && (
        <div className="space-y-3">
          <div className="text-sm font-cta font-semibold text-foreground">
            Uploaded Files ({formData.files.length})
          </div>
          <div className="space-y-2">
            {formData.files.map((file, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-4 bg-muted rounded-lg border border-border"
              >
                <div className="flex items-center space-x-3 flex-1 min-w-0">
                  <div className="flex-shrink-0 w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name="DocumentTextIcon" size={20} variant="outline" className="text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-cta font-semibold text-foreground truncate">
                      {file.name}
                    </div>
                    <div className="text-xs text-muted-foreground font-body">
                      {formatFileSize(file.size)}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => removeFile(index)}
                  className="flex-shrink-0 p-2 text-error hover:bg-error/10 rounded-lg transition-all duration-base"
                  aria-label="Remove file"
                >
                  <Icon name="XMarkIcon" size={20} variant="outline" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* File Requirements */}
      <div className="bg-accent/10 rounded-xl p-6 border border-accent/20">
        <div className="flex items-start space-x-3">
          <Icon name="InformationCircleIcon" size={24} variant="solid" className="text-accent flex-shrink-0" />
          <div className="space-y-2">
            <div className="text-sm font-cta font-semibold text-foreground">
              File Requirements
            </div>
            <ul className="text-sm text-muted-foreground font-body space-y-1">
              <li>• Accepted formats: DXF, STEP, STP, DWG, PDF, PNG, JPG</li>
              <li>• Maximum file size: 50MB per file</li>
              <li>• Include all views and dimensions in technical drawings</li>
              <li>• Multiple files can be uploaded for complex projects</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}